<template>
  <lb-dialog v-model="dialogVisible" heading="Bulk Upload Materiality" :width="hasData ? '' : '80%'" :loading="loading">
    <template v-slot:body>
      <div class="d-flex flex-column gap-3" data-testid="bulk-materiality-upload">        
        <div v-if="ruFileData.length > 0  || glFileData.length > 0 ||  slFileData.length > 0" class="" data-testid="bulk-materiality-upload__notes">
          <div class="d-flex flex-column gap-2">
            <h5 class="font-weight-medium">Notes</h5>
            <ol data-testid="bulk-materiality-upload__notes-list" class="pl-4 noteslist">
              <li 
                v-for="(note, index) in bulkMaterialityNotes" 
                :key="index"
                :data-testid="`bulk-materiality-upload__note-item-${index + 1}`"
              >
                {{ note }}
              </li>
            </ol>
          </div>
        </div>
      

        <!-- Tabs for RU, GL, SL -->
        <v-tabs v-model="selectedTab" background-color="" class="bulk-upload-tabs">
          <v-tab v-for="(tab, index) in uploadTabs" :key="index">
            {{ tab.label }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="selectedTab">
          <!-- RU Wise Tab -->
          <v-tab-item key="0" eager>
            <div>
              
                <!-- Cycle Type Toggle -->
        <div class="d-flex align-center">
          <lb-dropdown
            data-testid="sl-wise-materiality__statement-dropdown"
            v-model="selectedStatementType"
            :items="statementOptions"
            itemtext="labelName"
            itemvalue="name"
            roundedClass="rounded-lg"
            appendicon="mdi-chevron-down"
            dense
            hidedetails
            width="250px"
          />
          <v-btn-toggle
            v-model="bulkCycleType"
            mandatory
            class="pa-1 border-on rounded-lg gap-1 cycle-toggle"
            dense
            @change="onCycleTypeChange"
          >
            <v-btn :value="false" dense class="border-off" :class="{ 'primary': bulkCycleType === false }">
              <h5>{{ $nova.labelsName('materiality_page', 'actual_label', 'Actual') }}</h5>
            </v-btn>
            <v-btn :value="true" dense class="border-off" :class="{ 'primary': bulkCycleType === true }">
              <h5>{{ $nova.labelsName('materiality_page', 'forecast_longform_label', 'Forecast') }}</h5>
            </v-btn>
          </v-btn-toggle>
          </div>
            </div>

            <div class="d-flex align-center justify-end mb-3 mt-2">
              <v-btn color="primary" class="mr-2" @click="$emit('generateTemplate', 'rufile', selectedStatementType)">
                <span>Download Template</span>
              </v-btn>
              <v-spacer></v-spacer>
              <!-- Hidden file input for re-upload -->
              <input
                ref="ruReuploadInput"
                type="file"
                class="d-none"
                accept=".xlsx,.xls,.xlsm,.xlsb"
                @change="onReuploadFileChange('rufile', $event, selectedStatementType)"
              />
              <v-btn  
                v-if="ruFileData.length > 0 || (ruFile && ruFile.length > 0)"
                text 
                outlined
                @click="$refs.ruReuploadInput.click()"
                data-testid="bulk-materiality-upload__ru-reupload-btn"
              >
                <img src="/img/icons/FileUploadIcon.svg" class="file-upload-icon mr-2" alt="File Upload RU File"/>
                <span>Re-upload</span>
              </v-btn>
            </div>
            <div v-if="ruFileData.length === 0 && (!ruFile || ruFile.length === 0)" class="d-flex justify-center mt-4">
              <DragDropFileWithIcon 
                ref="ruFileInput"
                data-testid="bulk-materiality-upload__ru-file-upload"
                :key="'rufile-' + ruFileKey"
                :files.sync="ruFile"
                :loading="fileProcessing.rufile === 1"
                :multiplefiles="false"
                :resetTrigger="ruFileResetTrigger"
                accept=".xlsx,.xls,.xlsm,.xlsb"
                @update:files="onDragDropFileChange('rufile', $event, selectedStatementType)"
              />
            </div>
            <div v-if="ruFileData.length > 0 && ruFileData.length <= 1000">
              <v-simple-table dense class="v-table-color">
                <thead class="tableHeaderBg">
                  <tr>
                    <th scope="rucode" rowspan="3" class="noTransform">{{ $nova.labelsName('materiality_page', 'commenrary_ru_label', 'RU code') }}</th>
                    <th scope="runame" rowspan="3" class="noTransform">{{ $nova.labelsName('materiality_page', 'commenrary_ru_label', 'RU') }} Name</th>
                    <th scope="period" rowspan="3" class="noTransform">{{ $nova.labelsName('materiality_page', 'month_label', 'Period') }}</th>
                    <th scope="year" rowspan="3" class="noTransform">{{ $nova.labelsName('materiality_page', 'year_label', 'Year') }}</th>
                    <th scope="actual_cycle" colspan="16" class="noTransform" v-if="!bulkCycleType">{{ bulkActualLabel }} Cycle</th>
                    <th scope="forecast_cycle" colspan="16" class="noTransform" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Cycle</th>
                  </tr>
                  <tr>
                    <th scope="a_local_cycle" colspan="8" class="noTransform" v-if="!bulkCycleType">{{ bulkActualLabel }} Vs {{ bulkProformaLabel }}</th>
                    <th scope="a_regional_cycle" colspan="8" class="noTransform" v-if="!bulkCycleType">{{ bulkActualLabel }} Vs {{ bulkForecastShortLabel }}</th>
                    <th scope="f_local_cycle" colspan="8" class="noTransform" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Vs {{ bulkProformaLabel }}</th>
                    <th scope="f_regional_cycle" colspan="8" class="noTransform" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Vs Previous {{ bulkForecastShortLabel }}</th>
                  </tr>
                  <tr>
                    <th scope="lc" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_local_control_amount_shortform_label', 'LC Amt') }}</th>
                    <th scope="lc_percent" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_local_control_percentage_shortform_label', 'LC %') }}</th>
                    <th scope="lm" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_local_management_amount_shortform_label', 'LM Amt') }}</th>
                    <th scope="lm_percent" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_local_management_precentage_shortform_label', 'LM %') }}</th>
                    <th scope="rc" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_amount_shortform_label', 'RC Amt') }}</th>
                    <th scope="rc_percent" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_percentage_shortform_label', 'RC %') }}</th>
                    <th scope="rm" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_amount_shortform_label', 'RM Amt') }}</th>
                    <th scope="rm_percent" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_percentage_shortform_label', 'RM %') }}</th>
                    <th scope="lc" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_local_control_amount_shortform_label', 'LC Amt') }}</th>
                    <th scope="lc_percent" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_local_control_percentage_shortform_label', 'LC %') }}</th>
                    <th scope="lm" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_local_management_amount_shortform_label', 'LM Amt') }}</th>
                    <th scope="lm_percent" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_local_management_precentage_shortform_label', 'LM %') }}</th>
                    <th scope="rc" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_amount_shortform_label', 'RC Amt') }}</th>
                    <th scope="rc_percent" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_percentage_shortform_label', 'RC %') }}</th>
                    <th scope="rm" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_amount_shortform_label', 'RM Amt') }}</th>
                    <th scope="rm_percent" class="noTransform">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_percentage_shortform_label', 'RM %') }}</th>
                  </tr>
                </thead>
                <v-slide-y-transition class="" group tag="tbody" style="overflow:hidden !important;">
                  <tr v-for="(v, k) in paginatedRuFileData" :key="k">
                    <template v-for="(vv, kk) in ruFileColumns">
                      <td v-if="ruFileError[(ruPage - 1) * itemsPerPage + k] && ruFileError[(ruPage - 1) * itemsPerPage + k][vv]" :key="kk" class="error--text font-weight-bold error-cell-border">
                        <v-tooltip bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" v-if="vv === 'rucode' || vv === 'month' || vv === 'year'">{{ v[vv] || ruFileError[(ruPage - 1) * itemsPerPage + k][vv] || '' }}</span>
                            <span v-bind="attrs" v-on="on" v-else>{{ ruFileError[(ruPage - 1) * itemsPerPage + k][vv] || 'Error' }}</span>
                          </template>
                          <span>{{ ruFileError[(ruPage - 1) * itemsPerPage + k][vv] }}</span>
                        </v-tooltip>
                      </td>
                      <td v-else-if="vv === 'year'" :key="kk">
                        <div>{{ v['period'].split('-')[1] }}</div>
                      </td>
                      <td v-else-if="vv === 'month'" :key="kk">
                        <div>{{ v['period'].split('-')[0] }}</div>
                      </td>
                      <td v-else-if="vv === 'rucode'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                      <td v-else-if="vv === 'runame'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                      <td  class="bordervalues" v-else :key="kk">
                        <div class="bordervalues-cell">{{ v[vv] || '' }}</div>
                      </td>
                    </template>
                  </tr>
                </v-slide-y-transition>
              </v-simple-table>
              <div v-if="ruFileData.length > itemsPerPage" class="d-flex justify-start mt-3">
                <v-pagination
                  v-model="ruPage"
                  :length="ruTotalPages"
                  :total-visible="7"
                  data-testid="bulk-materiality-upload__ru-pagination"
                ></v-pagination>
              </div>
            </div>
            <div v-else-if="ruFileData.length !== 0 && ruFileData.length >= 1000" class="grey--text pa-6 text-center title">
              Preview will be unavailable for uploaded file containing more than 1,000 line items.
            </div>
            <div v-else class="grey--text pa-6 text-center title">
              Download the template, update the details and upload the file.
            </div>
          </v-tab-item>

          <!-- GL Wise Tab -->
          <v-tab-item key="1" eager>
            <div>
                <!-- Cycle Type Toggle -->
        <div class="d-flex align-center mb-3">
          <lb-dropdown
            data-testid="sl-wise-materiality__statement-dropdown"
            v-model="selectedStatementType"
            :items="statementOptions"
            itemtext="labelName"
            itemvalue="name"
            roundedClass="rounded-lg"
            appendicon="mdi-chevron-down"
            dense
            hidedetails
            width="250px"
          />
          <v-btn-toggle
            v-model="bulkCycleType"
            mandatory
            class="pa-1 border-on rounded-lg gap-1 cycle-toggle"
            dense
            @change="onCycleTypeChange"
          >
            <v-btn :value="false" dense class="border-off" :class="{ 'primary': bulkCycleType === false }">
              <h5>{{ $nova.labelsName('materiality_page', 'actual_label', 'Actual') }}</h5>
            </v-btn>
            <v-btn :value="true" dense class="border-off" :class="{ 'primary': bulkCycleType === true }">
              <h5>{{ $nova.labelsName('materiality_page', 'forecast_longform_label', 'Forecast') }}</h5>
            </v-btn>
          </v-btn-toggle>
          </div>
            </div>

            
            <div class="mb-2 d-flex align-center justify-end">
              <v-btn color="primary" class="mr-2" @click="$emit('generateTemplate', 'glfile', selectedStatementType)">
                <span>Download Template</span>
              </v-btn>
              <v-spacer></v-spacer>
              <!-- Hidden file input for re-upload -->
              <input
                ref="glReuploadInput"
                type="file"
                class="d-none"
                accept=".xlsx,.xls,.xlsm,.xlsb"
                @change="onReuploadFileChange('glfile', $event, selectedStatementType)"
              />
              <v-btn  
                v-if="glFileData.length > 0 ||(glFile && glFile.length > 0)"
                text 
                outlined
                @click="$refs.glReuploadInput.click()"
                data-testid="bulk-materiality-upload__gl-reupload-btn"
              >
                <img src="/img/icons/FileUploadIcon.svg" class="file-upload-icon mr-2" alt="File Upload GL File"/>
                <span>Re-upload</span>
              </v-btn>
            </div>
            <div v-if="glFileData.length === 0 && (!glFile || glFile.length === 0)" class="d-flex justify-center mt-4">
              <DragDropFileWithIcon 
                ref="glFileInput"
                data-testid="bulk-materiality-upload__gl-file-upload"
                :key="'glfile-' + glFileKey"
                :files.sync="glFile"
                :loading="fileProcessing.glfile === 1"
                :multiplefiles="false"
                :resetTrigger="glFileResetTrigger"
                accept=".xlsx,.xls,.xlsm,.xlsb"
                @update:files="onDragDropFileChange('glfile', $event, selectedStatementType)"
              />
            </div>
            <div v-if="glFileData.length > 0 && glFileData.length <= 1000">
              <v-simple-table dense class="v-table-color">
                <thead class="tableHeaderBg">
                  <tr>
                    <th scope="rucode" rowspan="4">{{ $nova.labelsName('materiality_page', 'commentary_ru_code_label', 'RU Code') }}</th>
                    <th scope="runame" rowspan="4">{{ $nova.labelsName('materiality_page', 'commenrary_ru_label', 'RU') }} Name</th>
                    <th scope="glcode" rowspan="4">{{ $nova.labelsName('materiality_page', 'commentary_gl_code_label', 'GL Code') }}</th>
                    <th scope="glname" rowspan="4">{{ $nova.labelsName('materiality_page', 'commentary_gl_shortform_label', 'GL') }} Name</th>
                    <th scope="period" rowspan="4">{{ $nova.labelsName('materiality_page', 'month_label', 'Period') }}</th>
                    <th scope="year" rowspan="3">{{ $nova.labelsName('materiality_page', 'year_label', 'Year') }}</th>
                    <th scope="actual_cycle" colspan="16" v-if="!bulkCycleType">{{ bulkActualLabel }} Cycle</th>
                    <th scope="forecast_cycle" colspan="16" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Cycle</th>
                  </tr>
                  <tr>
                    <th scope="a_local_cycle" colspan="8" v-if="!bulkCycleType">{{ bulkActualLabel }} Vs {{ bulkProformaLabel }}</th>
                    <th scope="a_regional_cycle" colspan="8" v-if="!bulkCycleType">{{ bulkActualLabel }} Vs {{ bulkForecastLongLabel }}</th>
                    <th scope="f_local_cycle" colspan="8" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Vs {{ bulkProformaLabel }}</th>
                    <th scope="f_regional_cycle" colspan="8" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Vs Previous {{ bulkForecastShortLabel }}</th>
                  </tr>
                  <tr>
                    <th scope="lc">{{ $nova.labelsName('materiality_page', 'commentary_local_control_amount_shortform_label', 'LC Amt') }}</th>
                    <th scope="lc_percent">{{ $nova.labelsName('materiality_page', 'commentary_local_control_percentage_shortform_label', 'LC %') }}</th>
                    <th scope="lm">{{ $nova.labelsName('materiality_page', 'commentary_local_management_amount_shortform_label', 'LM Amt') }}</th>
                    <th scope="lm_percent">{{ $nova.labelsName('materiality_page', 'commentary_local_management_precentage_shortform_label', 'LM %') }}</th>
                    <th scope="rc">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_amount_shortform_label', 'RC Amt') }}</th>
                    <th scope="rc_percent">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_percentage_shortform_label', 'RC %') }}</th>
                    <th scope="rm">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_amount_shortform_label', 'RM Amt') }}</th>
                    <th scope="rm_percent">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_percentage_shortform_label', 'RM %') }}</th>
                    <th scope="lc">{{ $nova.labelsName('materiality_page', 'commentary_local_control_amount_shortform_label', 'LC Amt') }}</th>
                    <th scope="lc_percent">{{ $nova.labelsName('materiality_page', 'commentary_local_control_percentage_shortform_label', 'LC %') }}</th>
                    <th scope="lm">{{ $nova.labelsName('materiality_page', 'commentary_local_management_amount_shortform_label', 'LM Amt') }}</th>
                    <th scope="lm_percent">{{ $nova.labelsName('materiality_page', 'commentary_local_management_precentage_shortform_label', 'LM %') }}</th>
                    <th scope="rc">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_amount_shortform_label', 'RC Amt') }}</th>
                    <th scope="rc_percent">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_percentage_shortform_label', 'RC %') }}</th>
                    <th scope="rm">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_amount_shortform_label', 'RM Amt') }}</th>
                    <th scope="rm_percent">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_percentage_shortform_label', 'RM %') }}</th>
                  </tr>
                </thead>
                <v-slide-y-transition class="" group tag="tbody" style="overflow:hidden !important;">
                  <tr v-for="(v, k) in paginatedGlFileData" :key="k">
                    <template v-for="(vv, kk) in glFileColumns">
                      <td v-if="glFileError[(glPage - 1) * itemsPerPage + k] && glFileError[(glPage - 1) * itemsPerPage + k][vv]" :key="kk" class="error--text font-weight-bold error-cell-border">
                        <v-tooltip bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" v-if="vv === 'glcode' || vv === 'rucode' || vv === 'month' || vv === 'year'">{{ v[vv] || glFileError[(glPage - 1) * itemsPerPage + k][vv] || '' }}</span>
                            <span v-bind="attrs" v-on="on" v-else>{{ glFileError[(glPage - 1) * itemsPerPage + k][vv] || 'Error' }}</span>
                          </template>
                          <span>{{ glFileError[(glPage - 1) * itemsPerPage + k][vv] }}</span>
                        </v-tooltip>
                      </td>
                      <td v-else-if="vv === 'year'" :key="kk">
                        <div>{{ v['period'].split('-')[1] }}</div>
                      </td>
                      <td v-else-if="vv === 'month'" :key="kk">
                        <div>{{ v['period'].split('-')[0] }}</div>
                      </td>
                       <td v-else-if="vv === 'rucode'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                      <td v-else-if="vv === 'runame'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                       <td v-else-if="vv === 'glname'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                      <td v-else-if="vv === 'glcode'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                       <td  class="bordervalues" v-else :key="kk">
                        <div class="bordervalues-cell">{{ v[vv] || '' }}</div>
                      </td>
                    </template>
                  </tr>
                </v-slide-y-transition>
              </v-simple-table>
              <div v-if="glFileData.length > itemsPerPage" class="d-flex justify-start mt-3">
                <v-pagination
                  v-model="glPage"
                  :length="glTotalPages"
                  :total-visible="7"
                  data-testid="bulk-materiality-upload__gl-pagination"
                ></v-pagination>
              </div>
            </div>
            <div v-else-if="glFileData.length !== 0 && glFileData.length >= 1000" class="grey--text pa-6 text-center title">
              Preview will be unavailable for uploaded file containing more than 1,000 line items.
            </div>
            <div v-else class="grey--text pa-6 text-center title">
              Download the template, update the details and upload the file.
            </div>
          </v-tab-item>

          <!-- SL Wise Tab -->
          <v-tab-item key="2" eager>
            <div>
                <!-- Cycle Type Toggle -->
        <div class="d-flex align-center mb-3">
           <lb-dropdown
        data-testid="sl-wise-materiality__statement-dropdown"
        v-model="selectedStatementType"
        :items="statementOptions"
        itemtext="labelName"
        itemvalue="name"
        roundedClass="rounded-lg"
        appendicon="mdi-chevron-down"
        dense
        hidedetails
        width="250px"
      />
          <v-btn-toggle
            v-model="bulkCycleType"
            mandatory
            class="pa-1 border-on rounded-lg gap-1 cycle-toggle"
            dense
            @change="onCycleTypeChange"
          >
            <v-btn :value="false" dense class="border-off" :class="{ 'primary': bulkCycleType === false }">
              <h5 class="font-weight-">{{ $nova.labelsName('materiality_page', 'actual_label', 'Actual') }}</h5>
            </v-btn>
            <v-btn :value="true" dense class="border-off" :class="{ 'primary': bulkCycleType === true }">
              <h5>{{ $nova.labelsName('materiality_page', 'forecast_longform_label', 'Forecast') }}</h5>
            </v-btn>
          </v-btn-toggle>
          </div>
            </div>
            <div class="mb-2 d-flex align-center justify-end">
              <v-btn color="primary" class="mr-2" @click="$emit('generateTemplate', 'slfile', selectedStatementType)">
                <span>Download Template</span>
              </v-btn>
              <v-spacer></v-spacer>
              <!-- Hidden file input for re-upload -->
              <input
                ref="slReuploadInput"
                type="file"
                class="d-none"
                accept=".xlsx,.xls,.xlsm,.xlsb"
                @change="onReuploadFileChange('slfile', $event, selectedStatementType)"
              />
              <v-btn  
                v-if="slFileData.length > 0 ||(slFile && slFile.length > 0)"
                text 
                outlined
                @click="$refs.slReuploadInput.click()"
                data-testid="bulk-materiality-upload__sl-reupload-btn"
              >
                <img src="/img/icons/FileUploadIcon.svg" class="file-upload-icon mr-2" alt="File Upload SL File"/>
                <span>Re-upload</span>
              </v-btn>
            </div>
            <div v-if="slFileData.length === 0 && (!slFile || slFile.length === 0)" class="d-flex justify-center mt-4">
              <DragDropFileWithIcon 
                ref="slFileInput"
                data-testid="bulk-materiality-upload__sl-file-upload"
                :key="'slfile-' + slFileKey"
                :files.sync="slFile"
                :loading="fileProcessing.slfile === 1"
                :multiplefiles="false"
                :resetTrigger="slFileResetTrigger"
                accept=".xlsx,.xls,.xlsm,.xlsb"
                @update:files="onDragDropFileChange('slfile', $event, selectedStatementType)"
              />
            </div>
            <div v-if="slFileData.length > 0 && slFileData.length <= 1000">
              <div class="mb-1">
                <strong>Note:</strong>
                <span v-if="isWPPClient" class="noTransform">
                  <br>1.&nbsp; All numbers reported in the Commentary Application are in USD thousands.
                  <br>2.&nbsp; The Local and Regional Control percentages are set to 999999% by default and cannot be edited.
                </span>
                <span class="noTransform">
                  <br>{{ isWPPClient ? '3.' : '1.' }}&nbsp; Materiality can be updated only for the current financial year.
                  <br>{{ isWPPClient ? '4.' : '2.' }}&nbsp; To update materiality at Drill-Down level for multiple periods, please use the bulk file upload option.
                </span>
              </div>
              <v-simple-table dense class="v-table-color">
                <thead class="tableHeaderBg">
                  <tr>
                    <th scope="rucode" rowspan="4">{{ $nova.labelsName('materiality_page', 'commentary_ru_code_label', 'RU Code') }}</th>
                    <th scope="runame" rowspan="4">{{ $nova.labelsName('materiality_page', 'commentary_ru_label', 'RU') }} Name</th>
                    <th scope="glcode" rowspan="4">{{ $nova.labelsName('materiality_page', 'commentary_gl_code_label', 'GL Code') }}</th>
                    <th scope="glname" rowspan="4">{{ $nova.labelsName('materiality_page', 'commentary_gl_shortform_label', 'GL') }} Name</th>
                    <th scope="period" rowspan="4">{{ $nova.labelsName('materiality_page', 'month_label', 'Period') }}</th>
                    <th scope="year" rowspan="3">{{ $nova.labelsName('materiality_page', 'year_label', 'Year') }}</th>
                    <th scope="actual_cycle" colspan="16" v-if="!bulkCycleType">{{ bulkActualLabel }} Cycle</th>
                    <th scope="forecast_cycle" colspan="16" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Cycle</th>
                  </tr>
                  <tr>
                    <th scope="a_local_cycle" colspan="8" v-if="!bulkCycleType">{{ bulkActualLabel }} Vs {{ bulkProformaLabel }}</th>
                    <th scope="a_regional_cycle" colspan="8" v-if="!bulkCycleType">{{ bulkActualLabel }} Vs {{ bulkForecastLongLabel }}</th>
                    <th scope="f_local_cycle" colspan="8" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Vs {{ bulkProformaLabel }}</th>
                    <th scope="f_regional_cycle" colspan="8" v-if="bulkCycleType">{{ bulkForecastLongLabel }} Vs Previous {{ bulkForecastShortLabel }}</th>
                  </tr>
                  <tr>
                    <th scope="lc">{{ $nova.labelsName('materiality_page', 'commentary_local_control_amount_shortform_label', 'LC Amt') }}</th>
                    <th scope="lc_percent">{{ $nova.labelsName('materiality_page', 'commentary_local_control_percentage_shortform_label', 'LC %') }}</th>
                    <th scope="lm">{{ $nova.labelsName('materiality_page', 'commentary_local_management_amount_shortform_label', 'LM Amt') }}</th>
                    <th scope="lm_percent">{{ $nova.labelsName('materiality_page', 'commentary_local_management_precentage_shortform_label', 'LM %') }}</th>
                    <th scope="rc">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_amount_shortform_label', 'RC Amt') }}</th>
                    <th scope="rc_percent">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_percentage_shortform_label', 'RC %') }}</th>
                    <th scope="rm">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_amount_shortform_label', 'RM Amt') }}</th>
                    <th scope="rm_percent">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_percentage_shortform_label', 'RM %') }}</th>
                    <th scope="lc">{{ $nova.labelsName('materiality_page', 'commentary_local_control_amount_shortform_label', 'LC Amt') }}</th>
                    <th scope="lc_percent">{{ $nova.labelsName('materiality_page', 'commentary_local_control_percentage_shortform_label', 'LC %') }}</th>
                    <th scope="lm">{{ $nova.labelsName('materiality_page', 'commentary_local_management_amount_shortform_label', 'LM Amt') }}</th>
                    <th scope="lm_percent">{{ $nova.labelsName('materiality_page', 'commentary_local_management_precentage_shortform_label', 'LM %') }}</th>
                    <th scope="rc">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_amount_shortform_label', 'RC Amt') }}</th>
                    <th scope="rc_percent">{{ $nova.labelsName('materiality_page', 'commentary_regional_control_percentage_shortform_label', 'RC %') }}</th>
                    <th scope="rm">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_amount_shortform_label', 'RM Amt') }}</th>
                    <th scope="rm_percent">{{ $nova.labelsName('materiality_page', 'commentary_regional_management_percentage_shortform_label', 'RM %') }}</th>
                  </tr>
                </thead>
                <v-slide-y-transition class="" group tag="tbody" style="overflow:hidden !important;">
                  <tr v-for="(v, k) in paginatedSlFileData" :key="k">
                    <template v-for="(vv, kk) in slFileColumns">
                      <td v-if="slFileError[(slPage - 1) * itemsPerPage + k] && slFileError[(slPage - 1) * itemsPerPage + k][vv]" :key="kk" class="error--text font-weight-bold error-cell-border">
                        <v-tooltip bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" v-if="vv === 'glcode' || vv === 'rucode' || vv === 'month' || vv === 'year'">{{ v[vv] || slFileError[(slPage - 1) * itemsPerPage + k][vv] || '' }}</span>
                            <span v-bind="attrs" v-on="on" v-else>{{ slFileError[(slPage - 1) * itemsPerPage + k][vv] || 'Error' }}</span>
                          </template>
                          <span>{{ slFileError[(slPage - 1) * itemsPerPage + k][vv] }}</span>
                        </v-tooltip>
                      </td>
                      <td v-else-if="vv === 'year'" :key="kk">
                        <div>{{ v['period'].split('-')[1] }}</div>
                      </td>
                      <td v-else-if="vv === 'month'" :key="kk">
                        <div>{{ v['period'].split('-')[0] }}</div>
                      </td>
                       <td v-else-if="vv === 'rucode'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                      <td v-else-if="vv === 'runame'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                       <td v-else-if="vv === 'glname'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                      <td v-else-if="vv === 'glcode'" :key="kk">
                        <div>{{ v[vv] }}</div>
                      </td>
                       <td  class="bordervalues" v-else :key="kk">
                        <div class="bordervalues-cell">{{ v[vv] || '' }}</div>
                      </td>
                    </template>
                  </tr>
                </v-slide-y-transition>
              </v-simple-table>
              <div v-if="slFileData.length > itemsPerPage" class="d-flex justify-start mt-3">
                <v-pagination
                  v-model="slPage"
                  :length="slTotalPages"
                  :total-visible="7"
                  data-testid="bulk-materiality-upload__sl-pagination"
                ></v-pagination>
              </div>
            </div>
            <div v-else-if="slFileData.length !== 0 && slFileData.length >= 1000" class="grey--text pa-6 text-center title">
              Preview will be unavailable for uploaded file containing more than 1,000 line items.
            </div>
            <div v-else class="grey--text pa-6 text-center title">
              Download the template, update the details and upload the file.
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>
    <template v-slot:actions>
      <div v-if="currentErrorFlag && emptyRuCode" class="error--text pr-4 font-weight-bold">
        RU code cannot be empty. Please provide valid value.
      </div>
      <div v-else-if="currentErrorFlag" class="error--text font-weight-bold">
        <span v-if="currentFileData.length > 1000">Preview unavailable: File contains over 1,000 line items and invalid inputs. Please correct the errors and re-upload.</span>
        <span v-else>Please add correct value</span>
      </div>
      <span v-else-if="currentFileData.length > 0" class="caption">*Required</span>
      <v-spacer></v-spacer>
      <v-btn 
        v-if="currentFileData.length > 0 && !currentErrorFlag"
        @click="handleProceedUpload" 
        color="primary" 
        class="ml-2 shadow-off"
      >
        <span>Proceed to Upload</span>
      </v-btn>
    </template>
  </lb-dialog>
</template>

<script>
import {
  STATEMENT_OPTIONS,
  RU_FILE_COLUMNS_ACTUAL,
  RU_FILE_COLUMNS_FORECAST,
  GL_FILE_COLUMNS_ACTUAL,
  GL_FILE_COLUMNS_FORECAST,
  SL_FILE_COLUMNS_ACTUAL,
  SL_FILE_COLUMNS_FORECAST,
  ITEMS_PER_PAGE
} from '../../utils/constants';
import { MATERIALITY } from '../../constants';
  import DragDropFileWithIcon from "../../../../../src/views/Inputs/DragDropFileWithIcon.vue"

export default {
  name: 'BulkMaterialityUpload',
  components: {
    DragDropFileWithIcon,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    ruFileData: {
      type: Array,
      default: () => [],
    },
    ruFileError: {
      type: Array,
      default: () => [],
    },
    ruFileErrorFlag: {
      type: Boolean,
      default: false,
    },
    glFileData: {
      type: Array,
      default: () => [],
    },
    glFileError: {
      type: Array,
      default: () => [],
    },
    glFileErrorFlag: {
      type: Boolean,
      default: false,
    },
    slFileData: {
      type: Array,
      default: () => [],
    },
    slFileError: {
      type: Array,
      default: () => [],
    },
    slFileErrorFlag: {
      type: Boolean,
      default: false,
    },
    fileProcessing: {
      type: Object,
      default: () => ({}),
    },
    emptyRuCode: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: Number,
      default: 0,
    },
    initialCycleType: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploadTabs: MATERIALITY.UPLOAD_TABS.map(tab => ({
        label: this.$nova.labelsName('materiality_page', tab.labelKey, tab.labelFallback),
        value: tab.value
      })),
      selectedTab: 0,
      bulkCycleType: false,
      ruFile: null,
      glFile: null,
      slFile: null,
      ruFileColumns: RU_FILE_COLUMNS_ACTUAL,
      glFileColumns: GL_FILE_COLUMNS_ACTUAL,
      slFileColumns: SL_FILE_COLUMNS_ACTUAL,
      // Keys to force re-render of file inputs for same file re-upload validation
      ruFileKey: 0,
      glFileKey: 0,
      slFileKey: 0,
      // Reset triggers for DragDropFileWithIcon components
      ruFileResetTrigger: false,
      glFileResetTrigger: false,
      slFileResetTrigger: false,
      selectedStatementType: 'pl',
      statementOptions: STATEMENT_OPTIONS,
      // Pagination
      ruPage: 1,
      glPage: 1,
      slPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    hasData() {
      return this.currentFileData.length > 0;
    },
    currentFileData() {
      switch (this.selectedTab) {
        case 0: return this.ruFileData;
        case 1: return this.glFileData;
        case 2: return this.slFileData;
        default: return [];
      }
    },
    currentErrorFlag() {
      switch (this.selectedTab) {
        case 0: return this.ruFileErrorFlag;
        case 1: return this.glFileErrorFlag;
        case 2: return this.slFileErrorFlag;
        default: return false;
      }
    },
    currentFileType() {
      return this.uploadTabs[this.selectedTab]?.value || 'rufile';
    },
    /**
     * Get bulk materiality notes from constants
     */
    bulkMaterialityNotes() {
      return [...MATERIALITY.NOTES.COMMON, ...MATERIALITY.NOTES.EDIT_MODE];
    },
    // Paginated data for RU table
    paginatedRuFileData() {
      if (this.ruFileData.length <= this.itemsPerPage) return this.ruFileData;
      const start = (this.ruPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.ruFileData.slice(start, end);
    },
    ruTotalPages() {
      return Math.ceil(this.ruFileData.length / this.itemsPerPage);
    },
    // Paginated data for GL table
    paginatedGlFileData() {
      if (this.glFileData.length <= this.itemsPerPage) return this.glFileData;
      const start = (this.glPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.glFileData.slice(start, end);
    },
    glTotalPages() {
      return Math.ceil(this.glFileData.length / this.itemsPerPage);
    },
    // Paginated data for SL table
    paginatedSlFileData() {
      if (this.slFileData.length <= this.itemsPerPage) return this.slFileData;
      const start = (this.slPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.slFileData.slice(start, end);
    },
    slTotalPages() {
      return Math.ceil(this.slFileData.length / this.itemsPerPage);
    },
    bulkActualLabel() {
      return this.selectedStatementType === 'bs'
        ? this.$nova.labelsName('cycle_page', 'actual_label_balancesheet', 'Actual')
        : this.$nova.labelsName('materiality_page', 'actual_label', 'Actual');
    },
    bulkProformaLabel() {
      return this.selectedStatementType === 'bs'
        ? this.$nova.labelsName('cycle_page', 'proforma_shortform_label_balancesheet', 'Proforma')
        : this.$nova.labelsName('materiality_page', 'proforma_longform_label', 'Proforma');
    },
    bulkForecastLongLabel() {
      return this.selectedStatementType === 'bs'
        ? this.$nova.labelsName('cycle_page', 'forecast_shortform_label_balancesheet', 'Forecast')
        : this.$nova.labelsName('materiality_page', 'forecast_longform_label', 'Forecast');
    },
    bulkForecastShortLabel() {
      return this.selectedStatementType === 'bs'
        ? this.$nova.labelsName('cycle_page', 'forecast_shortform_label_balancesheet', 'Forecast')
        : this.$nova.labelsName('materiality_page', 'forecast_shortform_label', 'Forecast');
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.selectedTab = this.initialTab;
        this.bulkCycleType = this.initialCycleType;
      }
    },
    bulkCycleType(newVal) {
      this.updateColumns();
      this.$emit('update:cycleType', newVal);
    },
  },
  methods: {
    /**
     * Handles file change from DragDropFileWithIcon component
     * @param {string} fileType - Type of file (rufile, glfile, slfile)
     * @param {Array} files - Array of files from the component
     */
    onDragDropFileChange(fileType, files, reporting_type) {    
      // DragDropFileWithIcon emits an array, get the first file for single file uploads
      const file = Array.isArray(files) && files.length > 0 ? files[0] : null;
      
      if (file) {
        // Emit the file change event for validation
        this.$emit('fileChange', { fileType, file, cycleType: this.bulkCycleType, reportingType:reporting_type });
      }
    },
    /**
     * @deprecated Use onDragDropFileChange instead - kept for backward compatibility
     */
    onFileChange(fileType) {
      let file = null;
      switch (fileType) {
        case 'rufile': file = this.ruFile; break;
        case 'glfile': file = this.glFile; break;
        case 'slfile': file = this.slFile; break;
      }
      
      // Always emit the file change event for validation, even if same file
      if (file) {
        this.$emit('fileChange', { fileType, file, cycleType: this.bulkCycleType });
        
        // Reset the file input and increment key to allow re-uploading the same file
        this.$nextTick(() => {
          switch (fileType) {
            case 'rufile': 
              this.ruFile = null; 
              this.ruFileKey++;
              break;
            case 'glfile': 
              this.glFile = null; 
              this.glFileKey++;
              break;
            case 'slfile': 
              this.slFile = null; 
              this.slFileKey++;
              break;
          }
        });
      }
    },
    onCycleTypeChange() {
      this.$emit('cycleTypeChange', this.bulkCycleType);
    },
    handleProceedUpload() {
      this.$emit('proceedUpload', { fileType: this.currentFileType, cycleType: this.bulkCycleType, reporting_type: this.selectedStatementType });
    },
    updateColumns() {
      if (this.bulkCycleType) {
        // Forecast columns
        this.ruFileColumns = RU_FILE_COLUMNS_FORECAST;
        this.glFileColumns = GL_FILE_COLUMNS_FORECAST;
        this.slFileColumns = SL_FILE_COLUMNS_FORECAST;
      } else {
        // Actual columns
        this.ruFileColumns = RU_FILE_COLUMNS_ACTUAL;
        this.glFileColumns = GL_FILE_COLUMNS_ACTUAL;
        this.slFileColumns = SL_FILE_COLUMNS_ACTUAL;
      }
    },
    /**
     * Handles file selection from re-upload hidden input
     * @param {string} fileType - Type of file (rufile, glfile, slfile)
     * @param {Event} event - The file input change event
     */
    onReuploadFileChange(fileType, event,selectedStatementType) {
      const files = event.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        // Emit the file change event for validation/processing
        this.$emit('fileChange', { fileType, file, cycleType: this.bulkCycleType, reportingType: selectedStatementType });
      }
      // Reset the input so the same file can be selected again
      event.target.value = '';
    },
  },
};
</script>

<style scoped>
.bulk-upload-tabs {
  border-bottom: 1px solid var(--grey-100);
  border-radius: 0;
}
.bulk-upload-tabs .v-tab {
  padding: 4px 16px;
  min-width: auto;
  font-size: 16px;
  font-weight: 400;
}
.bulk-upload-tabs .v-tab--active {
  font-weight: 600;
}

.bulk-upload-tabs::v-deep .v-tabs-bar {
  height: 34px;
}
.cycle-toggle {
  border-radius: 4px;
  margin-left: 10px;
}
.cycle-toggle .v-btn {
  text-transform: none;
  letter-spacing: normal;
  min-width: 100px;
}
.cycle-toggle .v-btn:before {
  background-color: transparent;
}
.cycle-toggle .v-btn.primary {
  color: white !important;
}
.border-on {
  border: 1px solid #e0e0e0;
}
.border-off {
  border: none !important;
}
.gap-1 {
  gap: 4px;
}

/* Error cell styling with red border */
.error-cell-border {
  border: 2px solid #ff5252 !important;
}
.v-table-color th {
 height: 48px !important;
}
.v-table-color td {
 height: 42px !important;
}
.bordervalues{
background: var(--grey-weak, #FAFAFA);
padding: var(--padding-2xs, 6px) var(--Space-2xs, 4px) !important;
}
.bordervalues .bordervalues-cell{
border-radius: var(--radius-sm, 8px);
border: 1px solid var(--grey-100, #E5E5E5);
background: var( --grey-white, #FFF);
padding: var(--Space-2xs, 4px) var(--Space-xs, 8px);
}
</style>
